import * as React from "react";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

const badgeVariants = cva(
  "inline-flex items-center justify-center gap-1.5 rounded-large border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 chip font-medium whitespace-nowrap select-none",
  {
    variants: {
      variant: {
        // Primary badge - solid filled primary (used in NavButton/NavDropdown for "New" labels)
        primary: "bg-cede-primary text-cede-contrast-shade-87 border-transparent",
        // Secondary badge - uses primary color with transparency
        secondary: "bg-cede-primary/10 text-cede-primary border-cede-primary/20",
        // Success badge - uses success color
        success: "bg-cede-success/10 text-cede-success border-cede-success/20",
        // Warning badge - uses warning color
        warning: "bg-cede-warning/10 text-cede-warning border-cede-warning/20",
        // Error badge - uses error color
        error: "bg-cede-error/10 text-cede-error border-cede-error/20",
        // Info badge - uses primary color with less opacity
        info: "bg-cede-primary/5 text-cede-primary border-cede-primary/10",
        // Outline badge - transparent background with border
        outline: "bg-transparent text-cede-text-primary border-cede-border-component",
        // Ghost badge - minimal styling
        ghost: "bg-transparent text-cede-text-secondary border-transparent",
        // Campaign status variants to match CampaignStatus component
        live: "bg-cede-success/10 text-cede-success border-cede-success/20",
        future: "bg-cede-primary/10 text-cede-primary border-cede-primary/20",
        past: "bg-cede-text-secondary/[0.08] text-cede-text-secondary border-cede-text-secondary/[0.16]",
        paused: "bg-cede-warning/10 text-cede-warning border-cede-warning/20",
      },
      size: {
        small: "px-2 py-0.5 text-xs",
        medium: "px-2.5 py-1 chip",
        large: "px-3 py-1.5 chip",
      },
      rounded: {
        true: "rounded-[400px]", // Fully rounded
        false: "rounded-large", // Uses theme border radius
      },
    },
    defaultVariants: {
      variant: "secondary",
      size: "medium",
      rounded: false,
    },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

function Badge({ className, variant, size, rounded, asChild = false, ...props }: BadgeProps) {
  const Comp = asChild ? Slot : "div";
  return <Comp className={cn(badgeVariants({ variant, size, rounded }), className)} {...props} />;
}

export { Badge, badgeVariants };
